import React from "react";
import AccordionTwoColumnGrid from "@/components/molecules/AccordionTwoColumnGrid";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Grid } from "@mantine/core";
import { Controller, Control } from "react-hook-form";
import { RegisterCompanyFormInput } from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";
import TextInput from "@/components/atoms/TextInput";
import PhoneField from "@/components/atoms/PhoneInput/PhoneInput";

interface ContactInformationProps {
  control: Control<RegisterCompanyFormInput>;
  saveDraft: () => Promise<void>;
}

const ContactInformation: React.FC<ContactInformationProps> = ({ control, saveDraft }) => {
  const { t } = useTranslation();

  const leftContent = (
    <Stack>
      <Text size="lg" display="technical" tt="uppercase" fw={500} ff="heading">
        {t("registerCompany:contactInformation.title")}
      </Text>
      <Grid>
        <Grid.Col span={1} pr={0} mr={0}>
          <SVGAsset name="circle-questionmark" />
        </Grid.Col>
        <Grid.Col span={10} pl={0} ml={0}>
          <Text size="sm" color="secondary">
            {t("registerCompany:contactInformation.reason")}
          </Text>
        </Grid.Col>
      </Grid>
    </Stack>
  );

  const rightContent = (
    <Grid>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          name="partialCompany.email"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <TextInput
              label={t("registerCompany:contactInformation.email")}
              value={value ?? ""}
              data-testid="email-input"
              onChange={onChange}
              required
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          name="partialCompany.phone"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState }) => (
            <PhoneField
              onBlur={async () => {
                onBlur();
                await saveDraft();
              }}
              value={value ?? ""}
              testId={"phone-input"}
              onChange={onChange}
              errormessage={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>
    </Grid>
  );

  return <AccordionTwoColumnGrid leftContent={leftContent} rightContent={rightContent} />;
};

export default ContactInformation;
